import { BehaviorSubject, combineLatest, defer, from, Observable, of } from 'rxjs';
import { switchMap, shareReplay, map, tap, first } from 'rxjs/operators';

import { Inverter, InverterV2, ConnextData } from '../../telemetry';
import {
  countHybrid,
  countStorage,
  countMeter,
  countEnerclic,
  count3Phase,
  isProductHybrid3Phase,
  isProductMeter,
  objectPropertiesToString,
  minToZero,
  applyFnToProperty,
  MathAvg,
  isNotNullOrUndefined,
  isProductEnerclic,
  clone,
  isProductStorage,
  isProductHybrid,
  parseSKonAggregateDailies,
} from "../../utils";
import { PhaseTypeCode, System, Device} from '../firebase.model';
import { ProductModelService } from '../product.model';
import { PowerFlowGraphMode } from '../telemetry.model';
import { TimezoneResponse } from '../weather.model';
import { Semaphore } from '../../utils/semaphore.utils';
import { StateService } from '../../state/state.service'
import { MemoizationPromiseFnBuilder } from '../../Memoization';

export type UISystem = ConnextSystem | VirtualSystem;
export type NodeType = 'solar' | 'grid' | 'battery' | 'home' | 'irradiance' | 'panelTemperature';
export type MonitoringType = 'Connext' | 'Solarman' | 'AzzurroHub';
export type PlantDescriptionLabels = {
  hybrid: string;
  photovoltaic: string;
  mixed: string;
  threephase_short: string;
  singlephase_short: string;
};
export type SystemType = 'virtual' | 'connext';
export type TelemetryModes = TelemetryMode[];
export type TelemetryMode =
  | 'telemetry/power-daily'
  | 'telemetry/energy-month'
  | 'telemetry/energy-year';
export type TelemetrySelfConsumeModes = TelemetrySelfConsumeMode[];
export type TelemetrySelfConsumeMode =
  | 'self-consume/self-consume-daily'
  | 'self-consume/self-consume-month'
  | 'self-consume/self-consume-year';
export type SystemMenuOptions = SystemMenuOption[];
export type SystemMenuOption =
  | 'energy'
  | 'self-consume'
  | 'evcharges'
  | 'plant';
export type RangeDatetimeOptions = RangeDatetimeOption[];
export type RangeDatetimeOption = 'date' | 'month-year' | 'year';
export type ConnextRequest = (connextId: string, refresh: boolean) => Promise<Partial<ConnextData>>;
export type VirtualRequest = (uid: string) => Promise<System>;
export type OnEventBuilder = (type: SystemType, uid: string) => Observable<boolean>;
export abstract class ISystem {
  type?: SystemType;
  support?: any;
  protected system: System;

  // eslint-disable-next-line class-methods-use-this
  get telemetryModes(): TelemetryModes {
    return [
      'telemetry/power-daily',
      'telemetry/energy-month',
      'telemetry/energy-year',
    ];
  }

  // eslint-disable-next-line class-methods-use-this
  get telemetrySelfConsumeModes(): TelemetrySelfConsumeModes {
    return [
      'self-consume/self-consume-daily',
      'self-consume/self-consume-month',
      'self-consume/self-consume-year',
    ];
  }

  // eslint-disable-next-line class-methods-use-this
  get rangeDateTimeOptions(): RangeDatetimeOptions {
    return ['date', 'month-year', 'year'];
  }

  constructor(system: System) {
    this.system = { ...system };
  }

  abstract getSystem(): Promise<System>;
  abstract saveSystem(system: System): Promise<void>;

  abstract getNodeTypes(): Observable<NodeType[]>;
  abstract getFlowGraphMode(): Observable<PowerFlowGraphMode>;
  /** Gets the timezone of the system in `quarter-hours` */
  abstract getTz(): Observable<number>;
  abstract getPType(): Observable<number>;
  abstract getCoordinates(): Observable<string>;
  abstract getSystemMenuOptions(): Observable<SystemMenuOptions>;
  abstract getSystemPowerAggregate(data: {
    [key: string]: Partial<Inverter>;
  }): Observable<Partial<Inverter>>;
  abstract getSystemEnergyAggregate(data: {
    [key: string]: Partial<Inverter>;
  }): Observable<Partial<Inverter>>;
  abstract getDailyPowerTelemetry(
    data: { [key: string]: Array<Partial<Inverter>> },
    date: Date,
    offset: number,
  ): Observable<Array<Partial<Inverter>>>;
  abstract getEnergyAggregate(data: {
    [key: string]: Array<Partial<Inverter>>;
  }): Observable<Array<Partial<Inverter>>>;

  filterBySourceData<T extends { [key: string ]: Array<Partial<Inverter>> } | Partial<Inverter>[]>(data: T): T {
    const isConnextPacket = this.type === 'connext';
    return filterBySourceData(data, isConnextPacket);
  }

  filterSomeAttributesFromPackets = filterSomeAttributesFromPackets;

  /** Only Connext */
  get id() {
    if (this.type === 'connext') {
      return this.system.configuration.connextId;
    }
    return undefined;
  }
}

/**
 * filters data based on the source (connext or virtual)
 */
const filterBySourceData = <T extends { [key: string ]: Array<Partial<Inverter>> } | Partial<Inverter>[]>
  (data: T, isConnextPacket: boolean): T => {
  const packetIsFromSystemSource = (d: Partial<Inverter>): boolean => {
    const gwId = d.GwId;
    if (!gwId) {
      /**
       * @note If gwId is not defined, it is not possible to know if the packet is from connext or virtual
       * so it is discarded
       */
      return false;
    }

    const loggerProduct = new ProductModelService(gwId);
    if (isConnextPacket) {
      return loggerProduct.type?.name === 'connext';
    }
    return loggerProduct.type?.name !== 'connext';
  }

  if (Array.isArray(data)) {
    return data.filter(packetIsFromSystemSource) as T;
  }

  const filteredData = Object.keys(data).reduce((acc, key) => {
    const filtered = data[key].filter(packetIsFromSystemSource);
    return {
      ...acc,
      [key]: filtered,
    };
  }, {});
  return filteredData as T;
};

const filterSomeAttributesFromPackets = <T extends { [key: string ]: Array<Partial<InverterV2>> } | Partial<InverterV2>[]>
  (data: T, attributes: Array<keyof InverterV2>): T => {
  const filterAttributes = (d: Partial<InverterV2>): Partial<InverterV2> => {
    const filtered = { ...d };
    attributes.forEach((attr) => {
      delete filtered[attr];
    });
    return filtered;
  };

  if (Array.isArray(data)) {
    return data.map(filterAttributes) as T;
  }

  const filteredData = Object.keys(data).reduce((acc, key) => {
    const filtered = data[key].map(filterAttributes);
    return {
      ...acc,
      [key]: filtered,
    };
  }, {});
  return filteredData as T;
};

export type UISystemOpts = {
  sync: false,
} | {
  sync: true,
  untilDestroyed: <U>(source: Observable<U>) => Observable<U>,
};

const systemContainer = new Map<string, ConnextSystem | VirtualSystem>();

const BuildUISystemInner = (
    system: System,
    fnC: ConnextRequest,
    fnV: VirtualRequest,
    onFn: OnEventBuilder,
    opts: UISystemOpts,
    stateService?: StateService,
  ) => {
  if (system.configuration.connextId) {
    return new ConnextSystem(system, fnC, fnV, onFn, opts, stateService);
  }
  return new VirtualSystem(system, stateService, fnV, onFn);
}

/**
 * @description Connext / Virtual System builder
 * @param system System
 * @param fnC connext data request construction function
 * @param fnV virtual data request construction function
 * @param onFn trigger event construction function
 * @param opts subscribe to system changes (inner to the system)
 */
export const BuildUISystem = (
  stateService: StateService | undefined,
  system: System,
  fnC: ConnextRequest,
  fnV: VirtualRequest,
  onFn: OnEventBuilder,
  key: string,
  opts: UISystemOpts = { sync: false },
): ConnextSystem | VirtualSystem => {
  const uid = system.uid!;

  /**
   * If sync is true, the system is created every time
   */
  if (opts.sync) {
    return BuildUISystemInner(system, fnC, fnV, onFn, opts, stateService);
  }

  const systemKey = `${key}-${uid}`;
  if (systemContainer.has(systemKey)) {
    return systemContainer.get(systemKey)!;
  }

  const systemInstance = BuildUISystemInner(system, fnC, fnV, onFn, opts, stateService);
  systemContainer.set(systemKey, systemInstance);
  return systemInstance;
};

const TimeZoneUrl = 'https://vip.timezonedb.com/v2.1/get-time-zone';
const TimeZoneApiKey = 'Y0UG9PIL0B2L';
export const getRequestTz = async (coordinates: string) => {
  const [lat, lng] = coordinates?.split(',');
  const params = {
    key: TimeZoneApiKey,
    format: 'json',
    by: 'position',
    lat,
    lng,
  };
  const url = new URL(TimeZoneUrl);
  for (const [key, param] of Object.entries(params)) {
    url.searchParams.append(key, param);
  }
  const response = await fetch(url.toString());
  console.log(`coordinates: ${coordinates} - update timezone offset`);
  if (response.status === 200) {
    const data = await response.json() as TimezoneResponse;
    if (data.status === 'OK') {
      // if zoneEnd is not present, use next month
      const nextMonthFromNowInSec = () => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() + 1, 1).getTime() / 1000;
      };
      return {
        ...data,
        zoneEnd: data.zoneEnd ? data.zoneEnd : nextMonthFromNowInSec(),
      }
    }
  }
  return null;
};

export const getRequestTzMemoized = MemoizationPromiseFnBuilder(getRequestTz, 1000 * 60 * 60 * 24);

/** SECTION -- VIRTUAL SYSTEM */

/**
 * Virtual System
 * Telemetry is sampling data from devices inverter/meter every 15 minutes
 * Inverter hybrid o storage -> With battery
 * Inverter photovoltaic + Meter -> Import and export
 * Inverter photovoltaic -> Only generation
 */
export class VirtualSystem extends ISystem {
  readonly type = 'virtual' as const;
  tzUpdating = false;

  system$: BehaviorSubject<System>;

  get uid(): string {
    if (this.system.uid) {
      return this.system.uid;
    }
    throw new Error('uid is not defined');
  }

  get name(): string {
    if (this.system$.value.name) {
      return this.system$.value.name;
    }
    throw new Error('name is not defined');
  }

  async getSystem(): Promise<System> {
    return this.system$.value;
  }

  async saveSystem(system: System): Promise<void> {
    this.system$.next(system);
  }

  constructor(system: System, stateService?: StateService, fn?: VirtualRequest, onFn?: OnEventBuilder, opts?: UISystemOpts) {
    super(system);

    this.system$ = new BehaviorSubject(system);
    if (stateService) {
      this.getSystem = async () => system.uid && stateService.getCurrentSystem(system.uid) || system;
      this.saveSystem = async (system) => stateService.saveCurrentSystem(system);
    }

    let _system$: Observable<System>;
    if (fn && onFn) {
      _system$ = defer(() => onFn(this.type, this.uid)).pipe(
        switchMap(() => fn(this.uid)),
        tap((system) => this.system$.next(system)),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    /** Different to ConnextSystem
     *  subscribe to system changes (inner to the system, is not optional)
    */
      if (opts?.sync) {
      _system$ = this.system$.pipe(
        opts.untilDestroyed,
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }

      _system$.subscribe();
    }

  }

  getNodeTypes(): Observable<NodeType[]> {
    return this.system$.pipe(
      map((system) => {
        const hasHybrid = countHybrid(system) > 0;
        const hasStorage = countStorage(system) > 0;
        if (hasHybrid || hasStorage) {
          return ['solar', 'grid', 'battery', 'home'];
        }
        const hasMeter = countMeter(system) > 0;
        const hasEnerclic = countEnerclic(system) > 0;
        if (hasMeter || hasEnerclic) {
          return ['solar', 'grid', 'home'];
        }
        return ['solar'];
      }),
    );
  }

  getFlowGraphMode(): Observable<PowerFlowGraphMode> {
    return this.system$.pipe(
      map((system) => {
        const hasHybrid = countHybrid(system) > 0;
        const hasStorage = countStorage(system) > 0;

        if (hasHybrid || hasStorage) {
          return PowerFlowGraphMode.WithBattery;
        }
        const hasMeter = countMeter(system) > 0;
        const hasEnerclic = countEnerclic(system) > 0;
        if (hasMeter || hasEnerclic) {
          return PowerFlowGraphMode.WithImportExport;
        }
        return PowerFlowGraphMode.OnlyGeneratedPower;
      }),
    );
  }



  getPlantType(): PowerFlowGraphMode {
    const hasHybrid = countHybrid(this.system) > 0;
    const hasStorage = countStorage(this.system) > 0;
    if (hasHybrid || hasStorage) {
      return PowerFlowGraphMode.WithBattery;
    }
    const hasMeter = countMeter(this.system) > 0;
    const hasEnerclic = countEnerclic(this.system) > 0;
    if (hasMeter || hasEnerclic) {
      return PowerFlowGraphMode.WithImportExport;
    }
    return PowerFlowGraphMode.OnlyGeneratedPower;
  }

  getCurrentTz(): Observable<number> {
    return this.system$.pipe(
      map((system) => {
        let tz = system.configuration.activeZone?.tz ?? system.configuration.temporaryZoneList[0]?.tz ?? 8;
        if (tz > 48 || tz < -48) {
          if (tz % 3600 === 0) {
            tz = (tz / 3600) * 4;
          } else {
            tz = 8; // default to UTC+2 Rome
          }
        }
        return tz;
      }),
    );
  }

  getTz(): Observable<any> {
    const _getTz = getSystemTz.bind(this);
    return from(_getTz(this));
  }

  getPType(): Observable<number> {
    return this.system$.pipe(
      map((system) => count3Phase(system)
      ? PhaseTypeCode.ThreePhase
      : PhaseTypeCode.MonoPhase),
    );
  }

  getCoordinates(): Observable<string> {
    return this.system$.pipe(
      map((system) =>
        system.configuration.temporaryZoneList[0]?.coordinates ||
        system.configuration.activeZone?.coordinates ||
        '',
      ),
    );
  }

  getSystemMenuOptions(): Observable<SystemMenuOptions> {
    return this.getFlowGraphMode().pipe(
      map((mode) => {
        if(mode === PowerFlowGraphMode.OnlyGeneratedPower) {
          return ['energy', 'plant'];
        }
        return ['energy', 'self-consume', 'plant'];
      }),
    );
  };

  getSnapshotPowerAggregate(telemetry: {
    [key: string]: Partial<Inverter>;
  }): Partial<Inverter> {
    const plantType = this.getPlantType();

    if(plantType === PowerFlowGraphMode.WithBattery) {
      return this.getHybridSystemPowerAggregate(telemetry);
    }
    if(plantType === PowerFlowGraphMode.WithImportExport) {
      return this.getImportExportSystemPowerAggregate(telemetry);
    }
    if(plantType === PowerFlowGraphMode.OnlyGeneratedPower) {
      return this.getPhotovoltaicSystemPowerAggregate(telemetry);
    }

    throw new Error('plant type not found');
  };

  getHybridSystemPowerAggregate(telemetry: {
    [key: string]: Partial<Inverter>;
  }): Partial<Inverter> {
    const devicesWhitBattery = Object.values(telemetry).filter(
      (v) =>
        (v.BP !== undefined && v.BP !== null && Number(v.BP) !== 0 && !Number.isNaN(Number(v.BP))) ||
        (v.BP1 !== undefined && v.BP1 !== null && Number(v.BP1) !== 0 && !Number.isNaN(Number(v.BP1))) ||
        (v.BP2 !== undefined && v.BP2 !== null && Number(v.BP2) !== 0 && !Number.isNaN(Number(v.BP2)))
    );
    // @note: we want insert on the first positions all inverters
    // necessary to have a correct power when there are meters on the system
    const devices = Object.keys(telemetry).map((id) => (new ProductModelService(id)))
    const sortedKeyDevices = devices.filter((pm) => pm.type?.name === 'inverter').concat(
      devices.filter((pm) => pm.type?.name !== 'inverter')
    ).map((pm) => pm.id);

    return objectPropertiesToString(
      sortedKeyDevices.reduce(
        (prev, deviceId) => {
          const cT: Partial<Inverter & InverterV2> = clone(telemetry[deviceId]);
          const product = new ProductModelService(deviceId);
          // @note: for hybrid-3ph power-generated is Production = Mp1 + Mp2 + p9 (external production)
          // @note: for hybrid-3ph power charging(discharging) & power importing(exporting) using 1 variable (positive or negative)
          // @note: for hybrid-3ph has two batteries therefore two charging / discharge values
          if ((isProductHybrid(product)) || isProductStorage(product)) {
            if(parseInt(`${cT.V}`) !== 3) {

              // @note: for old telemetries we need to aggregate production and charge / discharge
              cT.P_DC_TOT = `${
                Number(cT.P_SELF || '') +
                Number(cT.P_EXT || '')
              }`;

              cT.P_B_AGGR = `${
                Number(cT.P_B1 || 0) + Number(cT.P_B2 || 0)
              }`;
            }

            const batCount = [cT.BP1, cT.BP2, cT.BP].filter(
              (b) => b !== null && b !== undefined && b !== '0' && Number(b) !== 0
            );
            cT.BP = `${
              Number(cT.BP1 || '') + Number(cT.BP2 || '') + Number(cT.BP || '')
            }`;
            cT.BP = `${Number(cT.BP) === 0 || batCount.length === 0 ? 0 : ( Number(cT.BP) / batCount.length)}`;
          } else {
            // @note ignore all the power incoming from not hybrid device
            cT.P_DC_TOT = '';
            cT.P_LOAD = '';
            cT.P_EXP = '';
            cT.P_B_AGGR = '';
          }

          // @note: only for 'Zp1es006n2p067' Fabrizio Bernini request //
          // Questo inverter non ha potenza consumata mai (nonostante ogni giorno segnali Enegia consumata)
          // tamponiamo calcolandola (questo valore solitamente lo prendiamo direttamente dall'inverter)
          if (deviceId.toLocaleLowerCase() === 'zp1es006n2p067'.toLocaleLowerCase() && Number(cT.P_LOAD) === 0) {
            cT.P_LOAD = `${Number(cT.P_DC_TOT) + Number(cT.P_EXP) + Number(cT.P_B_AGGR)}`;
          }

          delete cT.BP1;
          delete cT.BP2;
          delete cT.MP1;
          delete cT.MP2;
          delete cT.P7;
          delete cT.P8;

          return {
            P_DC_TOT: prev.P_DC_TOT + Number(cT.P_DC_TOT || ''),
            P_EXP: prev.P_EXP + Number(cT.P_EXP || ''),
            P_B_AGGR: prev.P_B_AGGR + Number(cT.P_B_AGGR || ''),
            P_LOAD: prev.P_LOAD + Number(cT.P_LOAD || ''),
            BP: prev.BP + (cT.BP ? Number(cT.BP || '') / devicesWhitBattery.length : 0),
            TS: prev.TS > Number(cT.TS) ? prev.TS : Number(cT.TS),
          };
        },
        {
          P_DC_TOT: 0,
          P_EXP: 0,
          P_B_AGGR: 0,
          P_LOAD: 0,
          BP: 0,
          TS: 0,
        }
      )
    );
  };


  getImportExportSystemPowerAggregate(telemetry: {
    [key: string]: Partial<Inverter>;
  }): Partial<Inverter> {
    // @note: we want insert on the first positions all inverters
    // necessary to have a correct power when there are meters on the system
    const devices = Object.keys(telemetry).map((id) => (new ProductModelService(id)));
    const invertMeterSensor = this.system$.value.configuration.invertMeterSensor;
    const sortedKeyDevices = devices.filter((pm) => pm.type?.name === 'inverter').concat(
      devices.filter((pm) => pm.type?.name !== 'inverter')
    ).map((pm) => pm.id);

    return objectPropertiesToString(
      sortedKeyDevices.reduce(
        (prev, deviceId) => {
          const cT: Partial<Inverter & InverterV2> = clone(telemetry[deviceId]);
          const product = new ProductModelService(deviceId);

          cT.P_DC_TOT = cT.P_SELF;
          if (invertMeterSensor || invertMeterSensor === undefined) {
            cT.P_EXP = cT.P_EXP ? `${Number(cT.P_EXP) * -1}` : '';
          }
          if (isProductMeter(product) || isProductEnerclic(product)) {
            cT.P_EXP = cT.P_EXP ?? '';
            if(Number(cT.P_EXP) > 0 ) {
              cT.P_LOAD = `${ Number(cT.P_LOAD || '') - Number(cT.P_EXP) }`;
            } else {
              cT.P_LOAD = `${ Number(cT.P_LOAD || '') + (Number(cT.P_EXP) * -1 ) }`;
            }

          } else {
            cT.P_LOAD = `${ Number(cT.P_LOAD || '') + Number(cT.P_DC_TOT || '') }`;
          }

          return {
            P_DC_TOT: prev.P_DC_TOT + Number(cT.P_DC_TOT || ''),
            P_EXP: prev.P_EXP + Number(cT.P_EXP || ''),
            P_B_AGGR: 0,
            P_LOAD: prev.P_LOAD + Number(cT.P_LOAD || ''),
            BP: 0,
            TS: prev.TS > Number(cT.TS) ? prev.TS : Number(cT.TS),
          };
        },
        {
          P_DC_TOT: 0,
          P_EXP: 0,
          P_B_AGGR: 0,
          P_LOAD: 0,
          BP: 0,
          TS: 0,
        }
      )
    );
  }
  getPhotovoltaicSystemPowerAggregate(telemetry: {
    [key: string]: Partial<Inverter>;
  }): Partial<Inverter> {

    // @note: we want insert on the first positions all inverters
    // necessary to have a correct power when there are meters on the system
    const devices = Object.keys(telemetry).map((id) => (new ProductModelService(id))).map((pm)=> pm.id)
    return objectPropertiesToString(
      devices.reduce(
        (prev, deviceId) => {
          const cT: Partial<Inverter & InverterV2> = clone(telemetry[deviceId]);

          cT.P_DC_TOT = cT.P_SELF;

          return {
            P_DC_TOT: prev.P_DC_TOT + Number(cT.P_DC_TOT || ''),
            P_EXP: 0,
            P_B_AGGR: 0,
            P_LOAD: 0,
            BP: 0,
            TS: prev.TS > Number(cT.TS) ? prev.TS : Number(cT.TS),
          };
        },
        {
          P_DC_TOT: 0,
          P_EXP: 0,
          P_B_AGGR: 0,
          P_LOAD: 0,
          BP: 0,
          TS: 0,
        }
      )
    );
  }
  getSystemPowerAggregate(telemetry: {
    [key: string]: Partial<Inverter>;
  }): Observable<Partial<Inverter>> {
    return of(this.getSnapshotPowerAggregate(telemetry))
  }

  getSnapshotEnergyAggregate(telemetry: {
    [key: string]: Partial<Inverter>;
  }): Partial<Inverter> {
    const hasHybrid3Phase = Object.keys(telemetry)
      .map((devId) => new ProductModelService(devId))
      .some((pm) => isProductHybrid3Phase(pm));

    const hasMeter = Object.keys(telemetry)
      .map((devId) => new ProductModelService(devId))
      .some((pm) => isProductMeter(pm));

    const hasEnerclic = Object.keys(telemetry)
      .map((devId) => new ProductModelService(devId))
      .some((pm) => isProductEnerclic(pm));
    // @note: we want insert on the first positions all inverters
    // necessary to have a correct power when there are meters on the system
    const devices = Object.keys(telemetry).map((id) => (new ProductModelService(id)))
    const sortedKeyDevices = devices.filter((pm) => pm.type?.name === 'inverter').concat(
      devices.filter((pm) => pm.type?.name !== 'inverter')
    ).map((pm) => pm.id);

    return objectPropertiesToString(
      sortedKeyDevices.reduce(
        (prev, deviceId) => {
          const cT: Partial<Inverter> = { ...telemetry[deviceId] };

          const product = new ProductModelService(deviceId);
          // @note: is there is hybrid-3ph, Energy Generate is EG1 otherwise contribute is zero
          // if hybrid-3ph not present, Energy Generate is sum of all devices
          if (hasHybrid3Phase && !isProductHybrid3Phase(product)) {
            cT.EG1 = '0';
            cT.ET1 = '0';
          }

          if (hasMeter || hasEnerclic) {
            /** Attention: From the origin the imported / exported energy is exchanged (only on meter devices) */
            let temp = cT.EG3;
            cT.EG3 = cT.EG2;
            cT.EG2 = temp;

            temp = cT.ET3;
            cT.ET3 = cT.ET2;
            cT.ET2 = temp;

            const invertMeterSensor = this.system$.value.configuration.invertMeterSensor;
            if (invertMeterSensor || invertMeterSensor === undefined) {
              temp = cT.EG3;
              cT.EG3 = cT.EG2;
              cT.EG2 = temp;

              temp = cT.ET3;
              cT.ET3 = cT.ET2;
              cT.ET2 = temp;
            }

            // @note: for system with pv and meter EG4 (consumed) = EG1 - EG2 + EG3 (Generated - exported + imported)
            if (isProductMeter(product) || isProductEnerclic(product)) {
              cT.EG4 = `${ Number(cT.EG4 || '') - Number(cT.EG2) + Number(cT.EG3 || '') }`;
              cT.ET4 = `${ Number(cT.ET4 || '') - Number(cT.ET2) + Number(cT.ET3 || '') }`;
            } else {
              cT.EG4 = `${ Number(cT.EG4 || '') + Number(cT.EG1 || '') }`;
              cT.ET4 = `${ Number(cT.ET4 || '') + Number(cT.ET1 || '') }`;
            }
          }

          return {
            ...minToZero({
              EG1: prev.EG1 + Number(cT.EG1 || ''),
              EG2: prev.EG2 + Number(cT.EG2 || ''),
              EG3: prev.EG3 + Number(cT.EG3 || ''),
              EG4: prev.EG4 + Number(cT.EG4 || ''),
              EG5: prev.EG5 + Number(cT.EG5 || ''),
              EG6: prev.EG6 + Number(cT.EG6 || ''),
              ET1: prev.ET1 + Number(cT.ET1 || ''),
              ET2: prev.ET2 + Number(cT.ET2 || ''),
              ET3: prev.ET3 + Number(cT.ET3 || ''),
              ET4: prev.ET4 + Number(cT.ET4 || ''),
              ET5: prev.ET5 + Number(cT.ET5 || ''),
              ET6: prev.ET6 + Number(cT.ET6 || ''),
              TS: prev.TS > Number(cT.TS) ? prev.TS : Number(cT.TS),
            }),
            SK: cT.SK,
          };
        },
        {
          EG1: 0,
          EG2: 0,
          EG3: 0,
          EG4: 0,
          EG5: 0,
          EG6: 0,
          ET1: 0,
          ET2: 0,
          ET3: 0,
          ET4: 0,
          ET5: 0,
          ET6: 0,
          TS: 0,
        }
      )
    );
  };

  getSystemEnergyAggregate(telemetry: {
    [key: string]: Partial<Inverter>;
  }): Observable<Partial<Inverter>> {
    return of(this.getSnapshotEnergyAggregate(telemetry))
  };

  getEnergyAggregate(telemetry: {
    [key: string]: Array<Partial<Inverter>>;
  }): Observable<Array<Partial<Inverter>>> {
    return this.getTz().pipe(
      map((tz) => {
        const supportTelemetry: SupportDailyTelemetry = {};
        Object.keys(telemetry).forEach((devId) => {
          telemetry[devId].forEach((snapshot) => {
            const dateString = parseSKonAggregateDailies(snapshot.SK);
            if (dateString) {
              supportTelemetry[dateString] = {
                ...(supportTelemetry[dateString] || {}),
                [devId]: snapshot,
              };
            }
          });
        });

        const result = Object.keys(supportTelemetry).map(
          (dateString): Partial<Inverter> =>
            this.getSnapshotEnergyAggregate(supportTelemetry[dateString])
        );

        return result;
      },
    ));
  };

  getDailyPowerTelemetry(
    data: { [key: string]: Array<Partial<Inverter>> },
    date: Date,
    offset: number,
  ): Observable<Array<Partial<Inverter>>> {
    const BIN_SIZE = 5;
    // get latest TS & first TS
    let firstTs = 0;
    let latestTs = 0;
    Object.values(data).forEach((device) => {
      const first = device[0];
      const last = device[device.length - 1];
      if ((last.TS || 0) > latestTs) {
        latestTs = last.TS || 0;
      }
      if ((first.TS || 0) < firstTs || firstTs === 0) {
        firstTs = first.TS || 0;
      }
    });

    date.setHours(0 - date.getTimezoneOffset() / 60 - Number(offset) / 4);
    const midnight = date.getTime();

    const nextMidnight = midnight + 86400000;

    const dailyMinutes = (nextMidnight - midnight) / 1000 / 60;

    const bins = Array.from(
      { length: dailyMinutes / BIN_SIZE },
      (_, i) => midnight + i * BIN_SIZE * 60 * 1000
    );

    const result = {} as { [key: string]: Array<Partial<Inverter>> };

    const deviceIds = Object.keys(data);
    deviceIds.forEach((deviceId) => {
      const updatedTelemetry: Array<Partial<Inverter>> = [];
      const telemetry = data[deviceId];
      // Helper to find prev/next sample around a timestamp in ms
      const findPrevNext = (targetMs: number) => {
        let prev: Partial<Inverter> | undefined;
        let next: Partial<Inverter> | undefined;
        for (let i = 0; i < telemetry.length; i++) {
          const tMs = Number(telemetry[i].TS || 0) * 1000;
          if (tMs <= targetMs) {
            prev = telemetry[i];
          }
          if (tMs > targetMs) {
            next = telemetry[i];
            break;
          }
        }
        return { prev, next };
      };

      const collectKeys = (samples: Array<Partial<Inverter>>) => {
        const set = new Set<keyof Inverter>();
        samples.forEach(s => Object.keys(s || {}).forEach((k) => set.add(k as keyof Inverter)));
        return Array.from(set);
      };

      bins.forEach((binStart) => {
        const binEnd = binStart + BIN_SIZE * 60 * 1000;
        const tsCenter = binStart + (BIN_SIZE * 60 * 1000) / 2;

        const inBin = telemetry.filter((t) => {
          const currentTs = Number(t.TS || 0) * 1000;
          return currentTs >= binStart && currentTs < binEnd;
        });

        const currentTelemetry: Partial<Inverter> = {};

        if (inBin.length > 0) {
          // Determine prev/next samples to bound the bin segments
          const firstIndex = telemetry.indexOf(inBin[0]);
          const lastIndex = telemetry.indexOf(inBin[inBin.length - 1]);
          const prevSample = firstIndex > 0 ? telemetry[firstIndex - 1] : undefined;
          const nextSample = lastIndex < telemetry.length - 1 ? telemetry[lastIndex + 1] : undefined;

          const segmentSamples: Array<Partial<Inverter>> = [];
          if (prevSample) segmentSamples.push(prevSample);
          segmentSamples.push(...inBin);
          if (nextSample) segmentSamples.push(nextSample);

          const keys = collectKeys(segmentSamples);

          keys.forEach((key: any) => {
            if (key === 'TS' || key === '__typename') return;
            let weighted = 0;
            let coverage = 0;
            for (let i = 0; i < segmentSamples.length; i++) {
              const curr = segmentSamples[i];
              const currMs = Number(curr.TS || 0) * 1000;
              const nextMs = (i < segmentSamples.length - 1) ? Number(segmentSamples[i + 1].TS || 0) * 1000 : binEnd;
              const segStart = Math.max(binStart, currMs);
              const segEnd = Math.min(binEnd, nextMs);
              const dur = Math.max(0, segEnd - segStart);
              if (dur <= 0) continue;
              const val = Number((curr as any)[key]);
              if (!Number.isFinite(val)) continue;
              weighted += val * dur;
              coverage += dur;
            }
            if (coverage > 0) {
              (currentTelemetry as any)[key] = weighted / coverage;
            } else {
              // Fallback to simple mean if durations not available
              const values = inBin.map((s) => Number((s as any)[key])).filter((v) => Number.isFinite(v));
              if (values.length > 0) {
                (currentTelemetry as any)[key] = MathAvg(values);
              }
            }
          });
        } else {
          // Interpolate at bin center for short gaps
          const { prev, next } = findPrevNext(tsCenter);
          const MAX_GAP_MS = 20 * 60 * 1000; // 20 minutes tolerance
          if (prev && next) {
            const prevMs = Number(prev.TS || 0) * 1000;
            const nextMs = Number(next.TS || 0) * 1000;
            const gap = nextMs - prevMs;
            if (gap > 0 && gap <= MAX_GAP_MS) {
              const ratio = (tsCenter - prevMs) / gap;
              const keys = collectKeys([prev, next]);
              keys.forEach((key: any) => {
                if (key === 'TS' || key === '__typename') return;
                const v1 = Number((prev as any)[key]);
                const v2 = Number((next as any)[key]);
                if (Number.isFinite(v1) && Number.isFinite(v2)) {
                  (currentTelemetry as any)[key] = v1 + (v2 - v1) * ratio;
                }
              });
            }
          }
        }

        updatedTelemetry.push({
          ...currentTelemetry,
          TS: tsCenter / 1000,
        });
      });

      result[deviceId] = updatedTelemetry;
    });

    let aggregateValues = bins.map((_, i) =>
      this.getSnapshotPowerAggregate(
        deviceIds.reduce(
          (curr, id) => ({ ...curr, [id]: result[id][i] || {} }),
          {} as { [key: string]: Partial<Inverter> }
        ),
      )
    );

    // filter data after the last TS
    aggregateValues = aggregateValues.filter((v) => Number(v.TS) <= latestTs);
    // filter data before the first TS
    aggregateValues = aggregateValues.filter((v) => Number(v.TS) >= firstTs);

    return of(aggregateValues);
  };
}

const BIN_SIZE = 5;

interface SupportDailyTelemetry {
  [date: string]: {
    [key: string]: Partial<Inverter>;
  };
}

/**
 * Connext System
 * sensor mode !== 4 -> no battery (its necessary to check this, new firmware can handle more granularity)
 * sensor mode === 4 -> with battery
 */
export class ConnextSystem extends ISystem {
  readonly type = 'connext' as const;
  tzUpdating = false;

  system$: BehaviorSubject<System>;
  data$: Observable<Partial<ConnextData>>;

  getDailyPowerTelemetry = getConnextDailyPowerTelemetry;
  getSystemPowerAggregate = getConnextSystemPowerAggregate;
  getSystemEnergyAggregate = getConnextSystemEnergyAggregate;
  getEnergyAggregate = getConnextEnergyAggregate;

  get connextId(): string {
    if (this.system.configuration.connextId) {
      return this.system.configuration.connextId;
    }
    throw new Error('id is not defined');
  }

  get uid(): string {
    if (this.system.uid) {
      return this.system.uid;
    }
    throw new Error('uid is not defined');
  }

  get name(): string {
    if (this.system$.value.name) {
      return this.system$.value.name;
    }
    throw new Error('name is not defined');
  }

  async getSystem(): Promise<System> {
    return this.system$.value;
  }

  async saveSystem(system: System): Promise<void> {
    this.system$.next(system);
  }

  constructor(system: System, fn: ConnextRequest, fnS: VirtualRequest, onFn: OnEventBuilder, opts: UISystemOpts, stateService?: StateService) {
    super(system);
    this.system$ = new BehaviorSubject(system);
    if (stateService) {
      this.getSystem = async () => system.uid && stateService.getCurrentSystem(system.uid) || system;
      this.saveSystem = async (system) => stateService.saveCurrentSystem(system);
    }

    /** the boolean data is needed to access the cache (false) or
     * to force the http request for the data (true) */
    this.data$ = defer(() => onFn(this.type, this.uid)).pipe(
      map((event) => !!event),
      switchMap((refresh) => fn(this.connextId, refresh)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    /** if sync === true, data$ is subscribe on constructor & remove when angular component is destroyed */
    if (opts.sync) {
      this.data$ = this.data$.pipe(
        opts.untilDestroyed,
        shareReplay({ bufferSize: 1, refCount: true }),
      );

      this.data$.subscribe();
    }

    let _system$ = defer(() => onFn(this.type, this.uid)).pipe(
      switchMap(() => fnS(this.uid)),
      tap((system) => this.system$.next(system)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    /** Different to ConnextSystem
     *  subscribe to system changes (inner to the system, is not optional)
    */
    if (opts?.sync) {
      _system$ = _system$.pipe(
        opts.untilDestroyed,
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }

    _system$.subscribe();
  }

  getNodeTypes(): Observable<NodeType[]> {
    return this.data$.pipe(
      map((data) => data.config?.sensor_mode),
      isNotNullOrUndefined(),
      map((sensor_mode) => sensor_mode === 4
          ? ['solar', 'grid', 'battery', 'home']
          : ['solar', 'grid', 'home'],
      ),
    );
  }

  getSystemMenuOptions(): Observable<SystemMenuOptions> {
    return this.data$.pipe(
      map((_) => {
        const hasEvcharge = this.system$.value.devices.some((device : Device) => device.type === 'evcharge');
        return hasEvcharge
          ? ['energy', 'self-consume', 'evcharges', 'plant']
          : ['energy', 'self-consume', 'plant'];
      }),
    );
  }

  getFlowGraphMode(): Observable<PowerFlowGraphMode> {
    return this.data$.pipe(
      map((data) => data.config?.sensor_mode),
      isNotNullOrUndefined(),
      map((sensor_mode) => sensor_mode === 4
          ? PowerFlowGraphMode.WithBattery
          : PowerFlowGraphMode.WithImportExport),
    );
  }


  getCurrentTz(): Observable<number> {
    return this.system$.pipe(
      map((system) => {
        let tz = system.configuration.activeZone?.tz ?? system.configuration.temporaryZoneList[0]?.tz ?? 8;
        if (tz > 48 || tz < -48) {
          if (tz % 3600 === 0) {
            tz = (tz / 3600) * 4;
          } else {
            tz = 8; // default to UTC+2 Rome
          }
        }
        return tz;
      }),
    );
  }

  getTz(): Observable<any> {
    const _getTz = getSystemTz.bind(this);
    return from(_getTz(this));
  }

  getPType(): Observable<number> {
    return this.data$.pipe(
      map((data) => data.config),
      isNotNullOrUndefined(),
      map((config) => Number(config.pType)),
    );
  }

  getCoordinates(): Observable<string> {
    return this.data$.pipe(
      map((data) => data.config),
      isNotNullOrUndefined(),
      map((config) => ({
        lat: config.latitude || 0,
        long: config.longitude || 0,
      })),
      map(({ lat, long }) => `${lat.toPrecision(5)}, ${long.toPrecision(5)}`),
    );
  }
};

const getConnextSystemPowerAggregate = (data: { [key: string]: Partial<Inverter> }): Observable<Partial<Inverter>> => {
  throw new Error('Method not implemented.');
};

const getConnextSystemEnergyAggregate = (data: { [key: string]: Partial<Inverter> }): Observable<Partial<Inverter>> => {
  throw new Error('Method not implemented.');
};

const getConnextEnergyAggregate = (data: { [key: string]: Array<Partial<Inverter>> }): Observable<Array<Partial<Inverter>>> => {
  throw new Error('Method not implemented.');
};

const getConnextDailyPowerTelemetry = (data: { [key: string]: Array<Partial<Inverter>> }, date: Date): Observable<Array<Partial<Inverter>>> => {
  throw new Error('Method not implemented.');
};

export class DeviceTelemetry {
  deviceId: string;
  private productModel: ProductModelService;
  private uiSystem: ConnextSystem | VirtualSystem;

  constructor(deviceId: string, uiSystem: ConnextSystem | VirtualSystem) {
    this.deviceId = deviceId;
    this.productModel = new ProductModelService(deviceId);
    this.uiSystem = uiSystem;
  }

  getSourceOptions(): NodeType[] {
    // Non vengono gestiti evcharge, connext e no-zcs
    const product = this.productModel;
    if (product.type?.name === 'meter' || product.type?.name === 'enerclic') {
      return ['grid'];
    }
    if (product.type?.name !== 'inverter') {
      return [];
    }
    if(product.type.value === 'pv') {
      return ['solar'];
    }
    return product.type.value === 'storage'
      ? ['battery']
      : ['solar', 'battery'];
  }

  filterBySourceData(data: Partial<Inverter>[]): Partial<Inverter>[] {
    const isConnextPacket = this.uiSystem.type === 'connext';
    return filterBySourceData(data, isConnextPacket);
  }

  filterSomeAttributesFromPackets = filterSomeAttributesFromPackets;

  getPowerTelemetry<T extends Partial<InverterV2> | Partial<InverterV2>[]>
    (data: T, attributesToRemove: Array<keyof InverterV2>): T {

    const system = this.uiSystem.system$.value;
    const invertMeterSensor = system.configuration.invertMeterSensor;
    const product = this.productModel;
    const handlePowerRawData = (d: Partial<InverterV2>): Partial<InverterV2> => {
      d.P_DC_TOT = d.P_SELF;
      if(isProductEnerclic(product) || isProductMeter(product)) {
        if (invertMeterSensor || invertMeterSensor === undefined) {
          d.P_EXP = `${Number(d.P_EXP || '') * -1 }`
        }
      }

      if (product.type?.name === 'inverter' && (product.type.value === 'hybrid-3ph' || product.type.value === 'hybrid' )) {
        const batCount = [d.BP1, d.BP2, d.BP].filter((b) => b !== null && b !== undefined && b !== '0');
        d.P_B_AGGR = `${Number(d.P_B1 || '') + Number(d.P_B2 || '') }`;
        if (batCount.length > 0) {
          d.BP = `${Number(d.BP1 || '') + Number(d.BP2 || '') + Number(d.BP || '')}`;
          d.BP = `${Number(d.BP) / batCount.length}`;
        } else {
          d.BP = '0';
        }
      }

      for (const key of attributesToRemove) {
        delete d[key];
      }

      return d;
    }

    if (Array.isArray(data)) {
      return data.map(handlePowerRawData) as T;
    }

    return handlePowerRawData(data) as T;
  }

  getEnergyTelemetry<T extends Partial<Inverter> | Partial<Inverter>[]>
    (data: T): T {

    const system = this.uiSystem.system$.value;
    const invertMeterSensor = system.configuration.invertMeterSensor;
    const product = this.productModel;
    const handlePowerRawData = (d: Partial<Inverter>): Partial<Inverter> => {

      if(isProductEnerclic(product) || isProductMeter(product)) {
        let temp = d.EG3;
        d.EG3 = d.EG2;
        d.EG2 = temp;

        temp = d.ET3;
        d.ET3 = d.ET2;
        d.ET2 = temp;

        if (invertMeterSensor || invertMeterSensor === undefined) {
          temp = d.EG3;
          d.EG3 = d.EG2;
          d.EG2 = temp;

          temp = d.ET3;
          d.ET3 = d.ET2;
          d.ET2 = temp;
        }
      }

      return d;
    }

    if (Array.isArray(data)) {
      return data.map(handlePowerRawData) as T;
    }

    return handlePowerRawData(data) as T;
  }
}

/**
 * In this way the synchronization operations (firebase) must be managed, the methods that use saving
 */
const getInnerVirtualTz = async (_this: VirtualSystem) => _this.system$.pipe(
  switchMap(async (system) => {
    if ((system.configuration.timezoneEnd ?? 0) < Date.now() && !_this.tzUpdating) {
      _this.tzUpdating = true;
      const coordinates = system.configuration.temporaryZoneList[0]?.coordinates ||
        system.configuration.activeZone?.coordinates || '41.884326767819786,12.480541269505478';
      const timezone = await getRequestTz(coordinates);
      if (timezone) {
        const timezoneEnd = timezone.zoneEnd * 1000;
        const currentSystem = JSON.parse(JSON.stringify(system)) as System;
        currentSystem.configuration.timezoneEnd = timezoneEnd;
        const location = {
          coordinates,
          tz: timezone.gmtOffset / 900,
        }
        currentSystem.configuration.temporaryZoneList = [location];
        currentSystem.configuration.activeZone = location;
        await _this.saveSystem({ ...currentSystem });

        _this.system$.next(currentSystem);
      }
      _this.tzUpdating = false;
    }
    return system;
  }),
  switchMap((_) => _this.getCurrentTz()),
  first(),
).toPromise();

const getInnerConnextTz = async (_this: ConnextSystem)  => combineLatest([
  _this.system$,
  _this.data$,
]).pipe(
  switchMap(async ([s, data]) => {
    const system = await _this.getSystem();
    const coordinatesAreValid = data.config?.latitude && data.config?.longitude;

    let coordinatesAreChanged = true;
    const coordinatesActZone = system.configuration.activeZone?.coordinates.split(',');
    if ( coordinatesActZone && coordinatesActZone.length > 1) {
      let longActZonePrecision = coordinatesActZone[1].trim().length - 1;
      let latActZonePrecision = coordinatesActZone[0].trim().length - 1;
      longActZonePrecision = longActZonePrecision > 0 ? longActZonePrecision : 1;
      latActZonePrecision = latActZonePrecision > 0 ? latActZonePrecision : 1;
      const longActZone = Number(coordinatesActZone[1]).toPrecision(longActZonePrecision);
      const latActZone = Number(coordinatesActZone[0]).toPrecision(latActZonePrecision);

      coordinatesAreChanged = data.config?.latitude?.toPrecision(latActZonePrecision) !== latActZone ||
        data.config?.longitude?.toPrecision(longActZonePrecision) !== longActZone;
    }
    if (
      (
        ((system.configuration.timezoneEnd ?? 0) < Date.now()) ||
        (coordinatesAreValid && coordinatesAreChanged)
      ) &&
      !_this.tzUpdating
    ) {
      _this.tzUpdating = true;

      const coordinates = data.config?.latitude && data.config.longitude
        ? `${data.config.latitude},${data.config.longitude}`
        : system.configuration.activeZone?.coordinates
          ? system.configuration.activeZone.coordinates
          : '41.884326767819786,12.480541269505478';
      const timezone = await getRequestTz(coordinates);
      if (timezone) {
        const timezoneEnd = timezone.zoneEnd * 1000;
        const currentSystem = JSON.parse(JSON.stringify(system)) as System;
        currentSystem.configuration.timezoneEnd = timezoneEnd;
        const location = {
          coordinates,
          tz: timezone.gmtOffset / 900,
        }
        currentSystem.configuration.temporaryZoneList = [location];
        currentSystem.configuration.activeZone = location;
        await _this.saveSystem({ ...currentSystem });

        _this.system$.next(currentSystem);
      }
      _this.tzUpdating = false;
    }
    return system;
  }),
  switchMap((_) => _this.getCurrentTz()),
  first(),
).toPromise();

const getInnerSystemTz = async (_this: ISystem) => {
  if (_this.type === 'connext') {
    return await getInnerConnextTz(_this as ConnextSystem);
  }
  return await getInnerVirtualTz(_this as VirtualSystem);
};

const getSystemTz = (new Semaphore).wrap(getInnerSystemTz)
